import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
import keyGenerator from 'utils/keys';
import { constants as loadConstants } from 'utils/loads';
// redux
import { useDispatch, useSelector } from 'react-redux';
import {
  actions as loadsActions,
  selectors as loadSelectors,
} from 'state/loads';
import { selectors as orderSelectors } from 'state/order';
// styles
import styled from 'styled-components';
import { Flex, gridSizes } from 'utils/style';
// components
import { Field, Icon } from '@ontruck/orc';
import FieldError from 'components/form/FieldError';

const MIN_VALUE = 1;
const MAX_VALUE =
  loadConstants.MAX_NUMBER_OF_PALLETS_PER_TYPE[
    loadConstants.PALLET_TYPES.EU_PALLET
  ];

const getInputId = ({ shipmentKey, loadKey }) =>
  keyGenerator.load({
    shipmentKey,
    loadKey,
    field: loadConstants.FIELDS.NUMBER_OF_ITEMS,
  });

const isMinDisabled = (value) => value <= MIN_VALUE;
const isMaxDisabled = ({
  maxReached,
  value,
  maxNumberOfItems,
  hasSizeErrors,
}) =>
  !hasSizeErrors &&
  !!maxNumberOfItems &&
  (maxReached || value >= (maxNumberOfItems || MAX_VALUE));

const NumberOfItems = ({ className, shipmentKey, loadKey }) => {
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation('NumberOfItems');
  const dispatch = useDispatch();

  const value = useSelector((state) =>
    loadSelectors.getNumberOfItems(state, shipmentKey, loadKey)
  );
  const maxReached = useSelector(orderSelectors.getMaxReached);
  const maxItemsInLoad = useSelector((state) =>
    orderSelectors.getMaxItemsInThisLoad(state, shipmentKey, loadKey)
  );
  const numberOfItemsError = useSelector((state) =>
    loadSelectors.getLoadError(
      state,
      shipmentKey,
      loadKey,
      loadConstants.FIELDS.NUMBER_OF_ITEMS
    )
  );
  const hasNumberOfItemsError = useMemo(
    () => !!numberOfItemsError?.length,
    [numberOfItemsError]
  );

  const widthError = useSelector((state) =>
    loadSelectors.getLoadError(
      state,
      shipmentKey,
      loadKey,
      loadConstants.FIELDS.ITEM_WIDTH
    )
  );

  const lengthError = useSelector((state) =>
    loadSelectors.getLoadError(
      state,
      shipmentKey,
      loadKey,
      loadConstants.FIELDS.ITEM_LENGTH
    )
  );

  const hasSizeErrors = useMemo(
    () => !!widthError || !!lengthError,
    [widthError, lengthError]
  );

  const hasErrors =
    !hasSizeErrors && hasNumberOfItemsError ? 'error' : 'not-error';

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    dispatch(loadsActions.onBlurLoadValue({ shipmentKey, loadKey }));
  }, [dispatch, setIsFocused, shipmentKey, loadKey]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    dispatch(loadsActions.onFocusLoadValue({ shipmentKey, loadKey }));
  }, [dispatch, setIsFocused, shipmentKey, loadKey]);

  const changeLoadValue = useCallback(
    (newValue) => {
      dispatch(
        loadsActions.changeLoadValue({
          shipmentKey,
          loadKey,
          value: newValue,
          field: loadConstants.FIELDS.NUMBER_OF_ITEMS,
          maxItemsInLoad,
        })
      );
      handleBlur();
    },
    [dispatch, shipmentKey, loadKey, maxItemsInLoad, handleBlur]
  );

  const handleChange = useCallback(
    (newValue) => {
      changeLoadValue(newValue);
    },
    [changeLoadValue]
  );

  const handleIncrement = useCallback(() => {
    const newValue = value + 1;
    changeLoadValue(newValue);
  }, [changeLoadValue, value]);

  const handleDecrement = useCallback(() => {
    const newValue = Math.max(value - 1, MIN_VALUE);
    changeLoadValue(newValue);
  }, [changeLoadValue, value]);

  const inputId = useMemo(
    () => getInputId({ shipmentKey, loadKey }),
    [shipmentKey, loadKey]
  );
  const minDisabled = useMemo(() => isMinDisabled(value), [value]);
  const maxDisabled = useMemo(
    () =>
      isMaxDisabled({
        maxReached,
        value,
        maxNumberOfItems: maxItemsInLoad,
        hasSizeErrors,
      }),
    [value, maxReached, maxItemsInLoad, hasSizeErrors]
  );

  const showErrorTooltip =
    hasErrors && isFocused && maxItemsInLoad !== undefined;

  return (
    <div className={className}>
      <button
        type="button"
        className="btn left"
        onClick={handleDecrement}
        disabled={minDisabled}
      >
        <Icon glyph="minus" size="16px" color="white" />
      </button>
      <div className="fieldContainer">
        {showErrorTooltip && (
          <FieldError>{t('error_label', { max: maxItemsInLoad })}</FieldError>
        )}
        <Field
          type="text"
          id={inputId}
          name={inputId}
          fieldClassName="fieldModifier"
          labelClassName="labelModifier"
          className={hasErrors}
          value={value}
          label={t('label')}
          onChange={(_, { value: newValue }) => handleChange(newValue)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          size="L"
          fullWidth
          maxLength="3"
          dataE2E="pallets_number"
          autofocus
        />
      </div>
      <button
        type="button"
        className="btn right"
        onClick={handleIncrement}
        disabled={maxDisabled}
      >
        <Icon glyph="plus" size="16px" color="white" />
      </button>
    </div>
  );
};

NumberOfItems.propTypes = {
  className: PropTypes.string,
  shipmentKey: PropTypes.string.isRequired,
  loadKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default styled(NumberOfItems)`
  ${Flex.row};
  position: relative;

  .btn {
    margin-top: 1px;
    height: 48px;
    width: 32px;
    background-color: var(--grey500);
    -webkit-appearance: none;
    border: 0;
    outline: none;

    &[disabled] {
      opacity: 0.5;
    }

    &:hover {
      cursor: pointer;
    }

    &.left {
      border-radius: ${gridSizes('S')} 0 0 ${gridSizes('S')};
    }

    &.right {
      border-radius: 0 ${gridSizes('S')} ${gridSizes('S')} 0;
    }
  }

  .fieldModifier {
    text-align: center;
  }

  .labelModifier {
    justify-content: center;
    width: 100%;
  }

  .fieldContainer {
    position: relative;
    flex: 1;
  }
`;
